import type {
  AreaTag,
  AreaTagLabelRenderContext,
} from '@ironview/interactive-viewer';

export interface PendingAreaTagTextChanges {
  name?: string;
  text_position?: [number, number];
  text_size?: number;
  text_rotation?: number;
}

export interface Ironview360AreaTagLabelProps {
  areaTag: AreaTag;
  context: AreaTagLabelRenderContext;
  pendingChanges?: PendingAreaTagTextChanges;
  defaultFontSize?: number;
  fixedFontSize?: number;
}

export const wrapAreaTagLabelLines = (
  text: string,
  maxCharsPerLine: number,
  maxLines: number,
): string[] => {
  const source = (text || '').trim();
  if (!source) return [''];
  const safeMaxChars = Math.max(4, maxCharsPerLine);
  const words = source.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  const pushCurrent = () => {
    if (current.trim()) lines.push(current.trim());
    current = '';
  };

  for (const word of words) {
    if (word.length > safeMaxChars) {
      if (current) pushCurrent();
      let start = 0;
      while (start < word.length) {
        lines.push(word.slice(start, start + safeMaxChars));
        start += safeMaxChars;
      }
      continue;
    }
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= safeMaxChars) {
      current = candidate;
    } else {
      pushCurrent();
      current = word;
    }
  }
  pushCurrent();

  if (lines.length <= maxLines) return lines;
  const sliced = lines.slice(0, maxLines);
  const last = sliced[maxLines - 1];
  sliced[maxLines - 1] =
    last.length > 1
      ? `${last.slice(0, Math.max(1, last.length - 1))}\u2026`
      : '\u2026';
  return sliced;
};

export const Ironview360AreaTagLabel = ({
  areaTag,
  context,
  pendingChanges = {},
  defaultFontSize = 16,
  fixedFontSize,
}: Ironview360AreaTagLabelProps) => {
  const currentName = pendingChanges.name || areaTag.name;
  const currentTextPosition =
    pendingChanges.text_position || areaTag.label?.position;
  const currentTextRotation =
    pendingChanges.text_rotation || areaTag.label?.rotation;
  const labelFontSize =
    fixedFontSize ??
    Math.max(
      12,
      pendingChanges.text_size || areaTag.label?.size || defaultFontSize,
    );
  const xs = context.points.map((point) => point[0]);
  const polygonWidth =
    xs.length > 0 ? Math.max(...xs) - Math.min(...xs) : labelFontSize * 6;
  const maxLabelWidth = Math.max(40, polygonWidth - 16);
  const maxCharsPerLine = Math.max(
    6,
    Math.floor(maxLabelWidth / (labelFontSize * 0.58)),
  );
  const wrappedLabelLines = wrapAreaTagLabelLines(
    currentName,
    maxCharsPerLine,
    4,
  );
  const lineHeight = labelFontSize * 1.15;
  const textCenterX = currentTextPosition?.[0] ?? context.centroid.x;
  const textCenterY = currentTextPosition?.[1] ?? context.centroid.y;
  const totalTextHeight = wrappedLabelLines.length * lineHeight;
  const longestLine = wrappedLabelLines.reduce(
    (max, line) => Math.max(max, line.length),
    0,
  );
  const padX = labelFontSize * 0.3;
  const padY = labelFontSize * 0.2;
  const bgWidth = longestLine * labelFontSize * 0.56 + padX * 2;
  const bgHeight = totalTextHeight + padY * 2;
  const firstLineY =
    textCenterY - ((wrappedLabelLines.length - 1) * lineHeight) / 2;
  const rotateAttr = currentTextRotation
    ? `rotate(${currentTextRotation} ${textCenterX} ${textCenterY})`
    : undefined;

  return (
    <>
      <rect
        x={textCenterX - bgWidth / 2}
        y={textCenterY - totalTextHeight / 2 - padY}
        width={bgWidth}
        height={bgHeight}
        rx={6}
        ry={6}
        fill="#ffffff"
        fillOpacity={0.94}
        stroke="#2563eb"
        strokeOpacity={0.72}
        strokeWidth={1}
        transform={rotateAttr}
        style={{
          pointerEvents: 'none',
          transition: 'fill 180ms ease, stroke 180ms ease',
        }}
      />
      <text
        className="unit-label-text"
        textAnchor="middle"
        dominantBaseline="middle"
        transform={rotateAttr}
        style={{
          font: `500 ${labelFontSize}px/1.25 "Inter", Helvetica, Arial, serif`,
          fill: '#1e40af',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {wrappedLabelLines.map((line, lineIndex) => (
          <tspan
            key={`${areaTag.id}-line-${lineIndex}`}
            x={textCenterX}
            y={firstLineY + lineIndex * lineHeight}
          >
            {line}
          </tspan>
        ))}
      </text>
    </>
  );
};
